import {
  CardStatus,
  type CardStatusType,
  ONE_TIME_BONUS_PERK_INDEX,
} from '../types';

export const MS_PER_SECOND = 1000;
export const MS_PER_MINUTE = MS_PER_SECOND * 60;
export const MS_PER_HOUR = MS_PER_MINUTE * 60;
export const MS_PER_DAY = MS_PER_HOUR * 24;
export const PERCENTAGE_100 = 100;

type TranslationFn = (key: string, options?: Record<string, unknown>) => string;

type IconName = 'present' | 'tickIcon' | 'lock';

export type FooterState =
  | 'unauthenticated'
  | 'completed-with-pending-bonus'
  | 'completed-with-activated-bonus'
  | 'completed-no-bonus'
  | 'in-progress-with-pending-bonus'
  | 'in-progress'
  | 'locked';

/**
 * Returns the appropriate icon name for a perk based on its index and availability
 */
export const getPerkIcon = (
  perkIndex: number,
  isFirstLevel: boolean,
  isAvailable: boolean
): IconName => {
  const isOneTimeBonus = perkIndex === ONE_TIME_BONUS_PERK_INDEX;
  if (isOneTimeBonus && !isFirstLevel) return 'present';
  return isAvailable ? 'tickIcon' : 'lock';
};

/**
 * Calculates calendar days until expiration in user's timezone
 * @returns Number of days until expiration, or null if already expired or no date provided
 */
export const calcDaysUntilExpiration = (
  validUntil: string | null,
  activatableUntil: string | null
): number | null => {
  const expirationDate = validUntil || activatableUntil;
  if (!expirationDate) return null;

  const now = new Date();
  const expiry = new Date(expirationDate);

  // Reset time to start of day for accurate day comparison
  const nowDate = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const expiryDate = new Date(
    expiry.getFullYear(),
    expiry.getMonth(),
    expiry.getDate()
  );

  const diffMs = expiryDate.getTime() - nowDate.getTime();
  const diffDays = Math.round(diffMs / MS_PER_DAY);

  return diffDays >= 0 ? diffDays : null;
};

/**
 * Returns a localized bonus expiration message based on days remaining
 */
export const getBonusExpirationMessage = (
  days: number | null,
  t: TranslationFn
): string | null => {
  if (days === null) return null;
  if (days === 0) return t('loyalty:bonusExpiresToday');
  if (days === 1) return t('loyalty:bonusExpiresTomorrow');
  return t('loyalty:bonusExpiresInDays', { value: days });
};

/**
 * Pads a number with leading zeros to 2 digits
 */
export const pad = (n: number): string => String(n).padStart(2, '0');

/**
 * Formats milliseconds into a time remaining string (e.g., "02d:05h:30m")
 */
export const formatTimeRemaining = (ms: number): string | null => {
  if (ms <= 0) return null;

  const days = Math.floor(ms / MS_PER_DAY);
  const hours = Math.floor((ms % MS_PER_DAY) / MS_PER_HOUR);
  const minutes = Math.floor((ms % MS_PER_HOUR) / MS_PER_MINUTE);

  return `${pad(days)}d:${pad(hours)}h:${pad(minutes)}m`;
};

/**
 * Days before the retention deadline at which the LP warning should appear.
 * 1-6 → 2, 7 → 3, 8 → 4, 9 → 5, 10-11 → 6, 12 → 7
 */
export const getWarningThresholdDays = (levelId: number): number => {
  if (levelId <= 6) return 2;
  if (levelId === 7) return 3;
  if (levelId === 8) return 4;
  if (levelId === 9) return 5;
  if (levelId <= 11) return 6;
  return 7;
};

/**
 * Calculates the retention percentage for progress bar marker
 */
export const calculateRetainPercentage = (
  isLastLevel: boolean,
  retainAmount: number,
  targetAmount: number
): number => {
  if (isLastLevel) return PERCENTAGE_100;
  if (targetAmount > 0) return (retainAmount / targetAmount) * PERCENTAGE_100;
  return 0;
};

/**
 * Determines the footer state based on authentication and card status
 */
export const getFooterState = (
  isAuthenticated: boolean,
  status: CardStatusType,
  hasBonusAwaitingActivation: boolean,
  isPrizeActivated: boolean
): FooterState => {
  if (!isAuthenticated) return 'unauthenticated';

  const isCompleted = status === CardStatus.COMPLETED;
  const isInProgress = status === CardStatus.IN_PROGRESS;

  if (isCompleted) {
    if (hasBonusAwaitingActivation) return 'completed-with-pending-bonus';
    if (isPrizeActivated) return 'completed-with-activated-bonus';
    return 'completed-no-bonus';
  }

  if (isInProgress) {
    if (hasBonusAwaitingActivation) return 'in-progress-with-pending-bonus';
    return 'in-progress';
  }

  return 'locked';
};
